import React, { useEffect, useState } from "react";
import Template from "./Template";
import { useActions } from "../redux/actions";
import * as adjustmentsActions from "../redux/actions/adjustments";
import * as practiceActions from "../redux/actions/practice";
import { Button, Checkbox, Dropdown, Form, Input, Label, Message, Table } from "semantic-ui-react";

function OdAdjustments() {
    const AdjustmentsActions = useActions(adjustmentsActions);
    const PracticeActions = useActions(practiceActions);

    const [practiceOpts, setPracticeOpts] = useState<any[]>([]);
    const [selectedPracticeIds, setSelectedPracticeIds] = useState<number[]>([]);
    const [includeHidden, setIncludeHidden] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [definitions, setDefinitions] = useState<any[]>([]);
    const [searchDone, setSearchDone] = useState(false);
    const [filterPractice, setFilterPractice] = useState<string>("all");

    const createInit = {
        ItemName: "",
        category: "AdjTypes",
        ItemValue: "",
        isHidden: false,
    };
    const [createForm, setCreateForm] = useState(createInit);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<{ ItemName: string; ItemValue: string; isHidden: boolean }>({
        ItemName: "", ItemValue: "", isHidden: false,
    });
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [bulkForm, setBulkForm] = useState<{ ItemName: string; isHidden: boolean }>({ ItemName: "", isHidden: false });
    const [operationResults, setOperationResults] = useState<{ practice: string; success: boolean; error?: string }[] | null>(null);

    useEffect(() => {
        PracticeActions.getAllPractices().then((res: any) => {
            const opts = res.map((item: any) => ({
                key: item.practiceId,
                text: item.practice.practice,
                value: item.practiceId,
            }));
            setPracticeOpts(opts);
        });
    }, []);

    // Flatten multi-practice results: each entry is { practice, success, data: [...] }
    const flattenResults = (res: any[]) => {
        const rows: any[] = [];
        res.forEach((entry: any) => {
            if (entry.success && Array.isArray(entry.data)) {
                entry.data.forEach((def: any) => {
                    rows.push({ ...def, _practice: entry.practice });
                });
            }
        });
        return rows;
    };

    const handleSearch = () => {
        if (!searchKeyword.trim()) {
            alert("Please enter a search keyword.");
            return;
        }
        const ids = selectedPracticeIds.length ? selectedPracticeIds : practiceOpts.map((o: any) => o.value);
        const params: any = { includeHidden };
        params.search = searchKeyword;

        AdjustmentsActions.getOdDefinitions(ids, params).then((res: any) => {
            setDefinitions(Array.isArray(res) ? flattenResults(res) : []);
            setSelectedKeys(new Set());
            setSearchDone(true);
        });
    };

    const handleEdit = (def: any) => {
        setEditingKey(`${def._practice}-${def.DefNum}`);
        setEditForm({
            ItemName: def.ItemName || "",
            ItemValue: def.ItemValue || "",
            isHidden: def.isHidden === true || String(def.isHidden) === "true",
        });
    };

    const handleEditSave = (def: any) => {
        const opt = practiceOpts.find((o: any) => o.text === def._practice);
        if (!opt) return;
        const payload = { practiceIds: [opt.key], DefNum: def.DefNum, ...editForm };
        AdjustmentsActions.updateOdAdjustment(payload).then((res: any) => {
            if (Array.isArray(res) && res[0]?.success) {
                setDefinitions(definitions.map((d: any) =>
                    d._practice === def._practice && d.DefNum === def.DefNum
                        ? { ...d, ...editForm }
                        : d
                ));
                setEditingKey(null);
                setOperationResults([{ practice: def._practice, success: true }]);
            } else {
                setOperationResults(Array.isArray(res)
                    ? res.map((r: any) => ({ practice: r.practice, success: false, error: r.error }))
                    : [{ practice: def._practice, success: false, error: 'Update failed.' }]);
            }
        });
    };

    const getVisibleRows = () => definitions.filter((d: any) => filterPractice === "all" || d._practice === filterPractice);

    const handleRowSelect = (key: string, checked: boolean) => {
        const next = new Set(selectedKeys);
        checked ? next.add(key) : next.delete(key);
        setSelectedKeys(next);
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedKeys(new Set(getVisibleRows().map((d: any) => `${d._practice}-${d.DefNum}`)));
        } else {
            setSelectedKeys(new Set());
        }
    };

    const isAllSelected = () => {
        const visible = getVisibleRows();
        return visible.length > 0 && visible.every((d: any) => selectedKeys.has(`${d._practice}-${d.DefNum}`));
    };

    const handleBulkUpdate = async () => {
        if (!bulkForm.ItemName.trim()) {
            alert("ItemName is required for bulk update.");
            return;
        }
        if (!window.confirm(`Update ${selectedKeys.size} adjustment(s)?`)) return;

        const selectedDefs = definitions.filter(
            (d: any) => selectedKeys.has(`${d._practice}-${d.DefNum}`)
        );

        const groups: Map<number, number[]> = new Map();
        for (const def of selectedDefs) {
            const opt = practiceOpts.find((o: any) => o.text === def._practice);
            if (!opt) continue;
            if (!groups.has(def.DefNum)) groups.set(def.DefNum, []);
            groups.get(def.DefNum)!.push(opt.key);
        }

        const promises = Array.from(groups.entries()).map(([defNum, practiceIds]) =>
            AdjustmentsActions.updateOdAdjustment({
                practiceIds,
                DefNum: defNum,
                ItemName: bulkForm.ItemName,
                isHidden: bulkForm.isHidden,
            })
        );

        const results = await Promise.all(promises);
        const allResults = results.flat();

        setOperationResults(allResults.map((r: any) => ({
            practice: r.practice,
            success: !!r.success,
            error: r.error
        })));

        setDefinitions(definitions.map((d: any) => {
            if (selectedKeys.has(`${d._practice}-${d.DefNum}`)) {
                return { ...d, ItemName: bulkForm.ItemName, isHidden: bulkForm.isHidden };
            }
            return d;
        }));
        setSelectedKeys(new Set());
        setBulkForm({ ItemName: "", isHidden: false });
    };

    const handleCreate = () => {
        if (!createForm.ItemName) {
            alert("ItemName is required.");
            return;
        }
        const ids = selectedPracticeIds.length ? selectedPracticeIds : practiceOpts.map((o: any) => o.value);
        const payload: any = {
            practiceIds: ids,
            ItemName: createForm.ItemName,
            category: createForm.category,
            isHidden: createForm.isHidden,
        };
        if (createForm.ItemValue) payload.ItemValue = createForm.ItemValue;

        AdjustmentsActions.createOdAdjustment(payload).then((res: any) => {
            if (Array.isArray(res)) {
                setOperationResults(res.map((r: any) => ({
                    practice: r.practice,
                    success: !!r.success,
                    error: r.error
                })));
                setCreateForm(createInit);
                setShowCreateForm(false);
                handleSearch();
            }
        });
    };

    return (
        <Template activeLink="od-adjustments">
            <div className="ui card">
                <div className="content">
                    <h2>OD Adjustments</h2>
                    <Form>
                        <Form.Group widths="equal">
                            <Form.Field>
                                <label>Practices</label>
                                <Dropdown
                                    placeholder="All Practices"
                                    options={practiceOpts}
                                    selection={true}
                                    search={true}
                                    multiple={true}
                                    value={selectedPracticeIds}
                                    onChange={(e, { value }) => setSelectedPracticeIds(value as number[])}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Search by ItemName</label>
                                <Input
                                    placeholder="Keyword..."
                                    value={searchKeyword}
                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                    onKeyPress={(e: any) => e.key === 'Enter' && handleSearch()}
                                />
                            </Form.Field>
                            <Form.Field style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
                                <Checkbox
                                    label="Include Hidden"
                                    checked={includeHidden}
                                    onChange={(e, { checked }) => setIncludeHidden(!!checked)}
                                />
                            </Form.Field>
                            <Form.Field style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                                <Button primary onClick={handleSearch}>Search</Button>
                                <Button onClick={() => setShowCreateForm(!showCreateForm)}>
                                    {showCreateForm ? "Cancel" : "Create Adjustment"}
                                </Button>
                            </Form.Field>
                        </Form.Group>
                    </Form>
                </div>
            </div>

            {showCreateForm && (
                <div className="ui card">
                    <div className="content">
                        <h3>Create Adjustment</h3>
                        <p className="text-muted">Will be created in {selectedPracticeIds.length ? `${selectedPracticeIds.length} selected practice(s)` : 'all practices'}.</p>
                        <Form>
                            <Form.Group widths="equal">
                                <Form.Field required>
                                    <label>ItemName</label>
                                    <Input
                                        value={createForm.ItemName}
                                        onChange={(e) => setCreateForm({ ...createForm, ItemName: e.target.value })}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <label>ItemValue (optional)</label>
                                    <Dropdown
                                        placeholder="Select..."
                                        selection={true}
                                        clearable={true}
                                        options={[{ key: '+', text: '+', value: '+' }, { key: '-', text: '-', value: '-' }]}
                                        value={createForm.ItemValue}
                                        onChange={(e, { value }) => setCreateForm({ ...createForm, ItemValue: value as string })}
                                    />
                                </Form.Field>
                                <Form.Field style={{ display: "flex", alignItems: "flex-end" }}>
                                    <Checkbox
                                        label="isHidden"
                                        checked={createForm.isHidden}
                                        onChange={(e, { checked }) => setCreateForm({ ...createForm, isHidden: !!checked })}
                                    />
                                </Form.Field>
                            </Form.Group>
                            <Button primary onClick={handleCreate}>Submit</Button>
                        </Form>
                    </div>
                </div>
            )}

            {operationResults && (
                <Message
                    onDismiss={() => setOperationResults(null)}
                    positive={operationResults.every(r => r.success)}
                    negative={operationResults.some(r => !r.success)}
                >
                    <Message.Header>
                        {operationResults.filter(r => r.success).length} succeeded, {operationResults.filter(r => !r.success).length} failed
                    </Message.Header>
                    <Message.List>
                        {operationResults.map((r, i) => (
                            <Message.Item key={i}>
                                <strong>{r.practice}</strong>: {r.success ? 'Success' : r.error || 'Failed'}
                            </Message.Item>
                        ))}
                    </Message.List>
                </Message>
            )}

            {searchDone && (
                <div className="ui card">
                    <div className="content">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <h3 style={{ margin: 0 }}>Results ({definitions.length})</h3>
                            <Dropdown
                                placeholder="Filter by Practice"
                                selection={true}
                                clearable={true}
                                options={Array.from(new Set(definitions.map((d: any) => d._practice))).map((p: any) => ({ key: p, text: p, value: p }))}
                                value={filterPractice === "all" ? "" : filterPractice}
                                onChange={(e, { value }) => setFilterPractice(value ? value as string : "all")}
                            />
                        </div>
                        {selectedKeys.size > 0 && (
                            <Form style={{ marginBottom: 12, width: '50%' }}>
                                <Form.Group widths="equal">
                                    <Form.Field>
                                        <label>{selectedKeys.size} row(s) selected — New ItemName</label>
                                        <Input placeholder="New ItemName" value={bulkForm.ItemName}
                                            onChange={(e) => setBulkForm({ ...bulkForm, ItemName: e.target.value })} />
                                    </Form.Field>
                                    <Form.Field style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
                                        <Checkbox label="isHidden" checked={bulkForm.isHidden}
                                            onChange={(_e, { checked }) => setBulkForm({ ...bulkForm, isHidden: !!checked })} />
                                    </Form.Field>
                                    <Form.Field style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                                        <Button primary onClick={handleBulkUpdate}>Bulk Update</Button>
                                        <Button onClick={() => setSelectedKeys(new Set())}>Clear Selection</Button>
                                    </Form.Field>
                                </Form.Group>
                            </Form>
                        )}
                        <Table celled={true}>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>
                                        <Checkbox label="All" checked={isAllSelected()} onChange={(_e, { checked }) => handleSelectAll(!!checked)} />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>Practice</Table.HeaderCell>
                                    <Table.HeaderCell>DefNum</Table.HeaderCell>
                                    <Table.HeaderCell style={{ width: '35%' }}>ItemName</Table.HeaderCell>
                                    <Table.HeaderCell style={{ width: '10%' }}>ItemValue</Table.HeaderCell>
                                    <Table.HeaderCell>isHidden</Table.HeaderCell>
                                    <Table.HeaderCell>Actions</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {definitions.filter((d: any) => filterPractice === "all" || d._practice === filterPractice).map((def: any, idx: number) => {
                                    const key = `${def._practice}-${def.DefNum}`;
                                    const isEditing = editingKey === key;
                                    return (
                                    <Table.Row key={`${key}-${idx}`}>
                                        <Table.Cell>
                                            <Checkbox checked={selectedKeys.has(key)} onChange={(_e, { checked }) => handleRowSelect(key, !!checked)} />
                                        </Table.Cell>
                                        <Table.Cell><Label>{def._practice}</Label></Table.Cell>
                                        <Table.Cell>{def.DefNum}</Table.Cell>
                                        <Table.Cell>
                                            {isEditing
                                                ? <Input size="mini" fluid value={editForm.ItemName} onChange={(e) => setEditForm({ ...editForm, ItemName: e.target.value })} />
                                                : def.ItemName}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {isEditing
                                                ? <Dropdown
                                                    selection={true}
                                                    clearable={true}
                                                    fluid
                                                    options={[{ key: '+', text: '+', value: '+' }, { key: '-', text: '-', value: '-' }]}
                                                    value={editForm.ItemValue}
                                                    onChange={(_e, { value }) => setEditForm({ ...editForm, ItemValue: value as string })}
                                                  />
                                                : def.ItemValue}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {isEditing
                                                ? <Checkbox checked={editForm.isHidden} onChange={(e, { checked }) => setEditForm({ ...editForm, isHidden: !!checked })} />
                                                : (String(def.isHidden) === "true" ? "Yes" : "No")}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {isEditing ? (
                                                <>
                                                    <Button size="mini" primary onClick={() => handleEditSave(def)}>Save</Button>
                                                    <Button size="mini" onClick={() => setEditingKey(null)}>Cancel</Button>
                                                </>
                                            ) : (
                                                <Button size="mini" onClick={() => handleEdit(def)}>Edit</Button>
                                            )}
                                        </Table.Cell>
                                    </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </div>
                </div>
            )}
        </Template>
    );
}

export default OdAdjustments;
